import { useState } from 'react';

import { Code, Plus, Trash2 } from 'lucide-react';

import { TagPicker } from '@/components/contacts/TagPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import { TemplateCodeEditor } from '../TemplateCodeEditor';
import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { UpdateContactNodeData } from '@repo/shared-types';

const FIELD_SUGGESTIONS = ['name', 'tags', 'data.source', 'data.notes', 'data.tags'];

interface UpdateContactConfigProps {
  data: UpdateContactNodeData;
  onChange: (data: UpdateContactNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const UpdateContactConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: UpdateContactConfigProps) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const update = (patch: Partial<UpdateContactNodeData>) => onChange({ ...data, ...patch });
  const isFetchOnly = !!data.fetchContactData;

  const updateMapping = (index: number, field: string, value: string) => {
    const mappings = data.mappings.map((m, i) => (i === index ? { ...m, [field]: value } : m));
    update({ mappings });
  };

  const addRow = () => update({ mappings: [...data.mappings, { field: '', value: '' }] });

  const removeRow = (index: number) =>
    update({ mappings: data.mappings.filter((_, i) => i !== index) });

  // The tag picker manages the mapping row with field "tags" (CSV value). A
  // templated value ({{...}}) keeps that row in the generic mappings editor.
  const tagsMappingIndex = data.mappings.findIndex((m) => m.field === 'tags');
  const tagsMapping = tagsMappingIndex >= 0 ? data.mappings[tagsMappingIndex] : undefined;
  const tagsIsTemplate = !!tagsMapping && tagsMapping.value.includes('{{');
  const selectedTags =
    tagsMapping && !tagsIsTemplate
      ? tagsMapping.value
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

  const setTags = (tags: string[]) => {
    const value = tags.join(', ');
    if (tags.length === 0) {
      if (tagsMappingIndex >= 0) removeRow(tagsMappingIndex);
      return;
    }
    if (tagsMappingIndex >= 0) updateMapping(tagsMappingIndex, 'value', value);
    else update({ mappings: [...data.mappings, { field: 'tags', value }] });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Node label"
        />
      </div>

      <div className="border-border/40 space-y-2 border-t pt-4">
        <div className="flex items-center justify-between">
          <label className="text-muted-foreground text-xs font-medium">
            Fetch only (no update)
          </label>
          <Switch
            checked={isFetchOnly}
            onCheckedChange={(checked) => update({ fetchContactData: checked || undefined })}
          />
        </div>
        <p className="text-muted-foreground text-[10px]">
          {isFetchOnly
            ? 'Looks up the contact and loads all fields into the workflow context. Downstream nodes can use {{contact.name}}, {{contact.data.*}}, etc.'
            : 'Find or create a contact by phone/email, then update its fields. The full contact (name, data, tags) is loaded into the workflow context either way, so downstream nodes can use {{contact.data.*}} just like fetch-only mode.'}
        </p>
      </div>

      <div className="border-border/40 space-y-2 border-t pt-4">
        <label className="text-muted-foreground text-xs font-medium">Contact Lookup</label>
        <p className="text-muted-foreground text-[10px]">
          {isFetchOnly
            ? 'Resolve the contact by ID, phone, or email. Supports {{variables}}.'
            : "Find or create a contact by ID, phone, or email. Supports {{variables}}. If left empty, uses the call's contact."}
        </p>
        <div className="space-y-1">
          <label className="text-muted-foreground text-[10px]">Contact ID</label>
          <VariableAutocomplete
            value={data.contactId ?? ''}
            onChange={(v) => update({ contactId: v || undefined })}
            placeholder="e.g. {{webhook.body.contactId}}"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 space-y-1">
            <label className="text-muted-foreground text-[10px]">Phone</label>
            <VariableAutocomplete
              value={data.phone ?? ''}
              onChange={(v) => update({ phone: v || undefined })}
              placeholder="e.g. {{webhook.body.phone}}"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-muted-foreground text-[10px]">Email</label>
            <VariableAutocomplete
              value={data.email ?? ''}
              onChange={(v) => update({ email: v || undefined })}
              placeholder="e.g. {{webhook.body.email}}"
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
            />
          </div>
        </div>
      </div>

      {!isFetchOnly && (
        <>
          <div className="border-border/40 space-y-2 border-t pt-4">
            <label className="text-muted-foreground text-xs font-medium">Tags</label>
            <p className="text-muted-foreground text-[10px]">
              Tags are added to the contact&apos;s existing tags. A typed tag replaces the
              contact&apos;s current tag of the same type. Manage types and colors in Settings →
              Sales Console.
            </p>
            {tagsIsTemplate ? (
              <p className="text-muted-foreground text-[10px] italic">
                Tags are set from a template in the field mappings below.
              </p>
            ) : (
              <TagPicker value={selectedTags} onChange={setTags} />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">Field Mappings</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={addRow}
              >
                <Plus size={12} />
                Add row
              </Button>
            </div>

            <div className="text-muted-foreground text-[10px]">
              Use &quot;name&quot; to update contact name, or &quot;data.*&quot; for nested JSON
              fields (e.g. data.source, data.notes)
            </div>

            {data.mappings.length === 0 && (
              <p className="text-muted-foreground text-xs italic">
                No mappings yet. Click &quot;Add row&quot; or use JSON body below.
              </p>
            )}

            {data.mappings.map((mapping, index) => (
              <div key={index} className="flex items-center gap-1">
                <Input
                  value={mapping.field}
                  onChange={(e) => updateMapping(index, 'field', e.target.value)}
                  placeholder="field"
                  className="flex-1"
                  list={`contact-field-suggestions-${index}`}
                />
                <datalist id={`contact-field-suggestions-${index}`}>
                  {FIELD_SUGGESTIONS.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <span className="text-muted-foreground text-xs">&rarr;</span>
                <VariableAutocomplete
                  value={mapping.value}
                  onChange={(v) => updateMapping(index, 'value', v)}
                  placeholder="value"
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                  className="flex-1"
                />
                <VariablePicker
                  onSelect={(v) => updateMapping(index, 'value', mapping.value + v)}
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                  triggerType={triggerType}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive h-8 w-8 p-0"
                  onClick={() => removeRow(index)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>

          <div className="border-border/40 space-y-2 border-t pt-4">
            <div className="flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">
                JSON Data Body (optional)
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => setEditorOpen(true)}
              >
                <Code size={12} />
                {data.dataBody ? 'Edit JSON' : 'Add JSON'}
              </Button>
            </div>
            <p className="text-muted-foreground text-[10px]">
              Merge a JSON object directly into the contact&apos;s data field. Supports{' '}
              {'{{variables}}'}.
            </p>
            {data.dataBody && (
              <pre className="bg-muted/30 max-h-32 overflow-auto rounded-md p-2 text-[10px]">
                {data.dataBody}
              </pre>
            )}
          </div>

          <TemplateCodeEditor
            value={data.dataBody ?? ''}
            onChange={(v) => update({ dataBody: v || null })}
            type="json"
            open={editorOpen}
            onOpenChange={setEditorOpen}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
          />
        </>
      )}
    </div>
  );
};
